import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldPlus, ShieldMinus, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, Avatar, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Directors() {
  const { user } = useAuth();
  const [directors, setDirectors] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const load = () => {
    api.get("/people/directors").then((r) => setDirectors(r.data));
    api.get("/people/accounts").then((r) => setAccounts(r.data));
  };
  useEffect(load, []);

  const adults = accounts.filter((a) => a.role === "teacher" || a.role === "parent");

  const promote = async (uid) => {
    try { await api.post("/people/directors/promote", { user_id: uid }); toast.success("Promoted to Director"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const demote = async (uid) => {
    try { await api.post("/people/directors/demote", { user_id: uid }); toast.success("Director access removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="stagger-in">
      <PageHeader title="Director Management" subtitle="Grant or remove full administrative access." testid="directors-page" />

      <h3 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><Crown className="w-5 h-5 text-teal" />Current Directors</h3>
      <div className="space-y-2 mb-8">
        {directors.map((d) => (
          <div key={d.user_id} data-testid={`director-row-${d.user_id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Avatar first={d.name} last="" photo={d.picture} size={42} color="#000000" />
              <div><p className="font-head font-bold">{d.name}</p><p className="text-xs text-muted-foreground">{d.email}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-teal-light text-teal-dark rounded-full flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Director</Badge>
              {directors.length > 1 && d.user_id !== user?.user_id && (
                <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`demote-${d.user_id}`} onClick={() => demote(d.user_id)}>
                  <ShieldMinus className="w-3.5 h-3.5 mr-1" />Remove
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <h3 className="font-head font-bold text-lg mb-3">Promote an adult to Director</h3>
      {adults.length === 0 ? <EmptyState icon={ShieldPlus} title="No adult accounts to promote" hint="Teachers and parents can be promoted." /> :
        <div className="space-y-2">
          {adults.map((a) => (
            <div key={a.user_id} data-testid={`adult-row-${a.user_id}`} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar first={a.name} last="" photo={a.picture} size={42} />
                <div><p className="font-semibold">{a.name}</p><p className="text-xs text-muted-foreground">{a.email} · <span className="capitalize">{a.role}</span></p></div>
              </div>
              <Button size="sm" className="rounded-full bg-teal hover:bg-teal-dark" data-testid={`promote-${a.user_id}`} onClick={() => promote(a.user_id)}>
                <ShieldPlus className="w-3.5 h-3.5 mr-1" />Make Director
              </Button>
            </div>
          ))}
        </div>}
    </div>
  );
}
